/**
 * Pure helpers for the reminders layer (B4.2) — phrasing, clear-permission, and
 * future-date validation. Kept dependency-free + in one place so the drawer
 * tab, the team bell, and the tests all read identically. No rendering here.
 *
 * Issue #768 (Phase 4). §A1.3 / §A9 firewall: every phrase a reminder produces
 * is a FORWARD nudge ("nudge about this on <date>") — never "when did X
 * happen", never a per-person activity record, never a tally / score. Copy is
 * calm and factual.
 */

import type { TaskReminder } from "../api/useReminders";

/** "May 24" — the short absolute target date a nudge points at. */
export function formatRemindDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * A calm "how far ahead" phrase for a forward nudge ("today", "tomorrow",
 * "in 2 days", or a date when it's further out). Always future-facing — a
 * reminder points forward (§A1.3); a value already in the past degrades to
 * "soon" rather than reading like an elapsed-time log.
 */
export function formatRemindLead(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dayMs = 24 * 60 * 60 * 1000;
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / dayMs);
  if (days <= 0) return "soon";
  if (days === 1) return "tomorrow";
  if (days < 7) return `in ${days} days`;
  return formatRemindDate(iso);
}

/**
 * Is `iso` a valid FUTURE instant? The single client-side guard mirroring the
 * backend's "remind_at must be in the future" 422 — so the compose box can
 * block a past date up front with a calm message instead of bouncing off a 422.
 * An empty / unparseable value is not valid (nothing to set).
 */
export function isFutureInstant(iso: string, now: Date = new Date()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() > now.getTime();
}

/**
 * May the actor clear this reminder? The reminder's setter or a lead / co_lead
 * only — mirrors the backend's 403 rule (`taskmap.reminders.clear_reminder`) so
 * the UI never offers a clear the API would reject. The backend records the
 * setter as `created_by = actor_membership_id`, so we compare against the
 * actor's MEMBERSHIP id (not their account id).
 */
export function canClearReminder(
  reminder: TaskReminder,
  currentMembershipId: string | null,
  isLead: boolean,
): boolean {
  if (isLead) return true;
  return currentMembershipId != null && reminder.created_by === currentMembershipId;
}

/** A reminder phrased for the team bell — a primary line + an optional lead line. */
export interface BellReminderLine {
  /** The factual nudge ("'<node>' has a target of <date>"). */
  primary: string;
  /** The "how far ahead" lead ("2 days away."), or null when it's the same day / past. */
  secondary: string | null;
}

/**
 * Phrase ONE reminder for the B4.2 bell feed (§A1.3 / §A9). Always a FORWARD
 * nudge about the work: *"'<node title>' has a target of <date>"* — with a calm
 * *"… is yours and …"* lead only when the nudge is for the viewer (orientation,
 * never a tally about anyone). An optional note rides as the primary line when
 * present; otherwise the target-date phrase is the primary. The secondary is
 * the lead phrase ("2 days away."). Never names *who else* a nudge is for — that
 * would read as a feed about other people (§A9); a not-for-me nudge stays
 * impersonal ("has a target of …").
 */
export function describeReminderForBell(
  reminder: TaskReminder,
  nodeTitle: string | undefined,
  isMine: boolean,
): BellReminderLine {
  const title = nodeTitle?.trim() || "A task";
  const note = reminder.note?.trim();
  const ownership = isMine ? "is yours and has" : "has";
  const primary = note
    ? `“${title}” — ${note}`
    : `“${title}” ${ownership} a target of ${formatRemindDate(reminder.remind_at)}`;
  return { primary, secondary: leadSentence(formatRemindLead(reminder.remind_at)) };
}

/**
 * Turn a lead phrase into a calm full-stop count-down line ("2 days away.",
 * "Tomorrow."), matching the B4.2 SVG's second line. Returns null for the
 * same-day / past "soon" case (no separate count-down — the primary stands).
 */
function leadSentence(lead: string): string | null {
  if (!lead || lead === "soon") return null;
  if (lead === "tomorrow") return "Tomorrow.";
  // "in 2 days" → "2 days away."; an absolute date ("May 24") → "May 24."
  if (lead.startsWith("in ")) return `${lead.slice(3)} away.`;
  return `${lead}.`;
}
